import { createAsyncThunk } from "@reduxjs/toolkit";
import { ColumnFiltersState } from "@tanstack/react-table";
import { Axios } from "api/axios";
import axios from "axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";

const API_URL = process.env.REACT_APP_BASE_URL;
const headers = {
  "Content-Type": "application/json",
};

export interface Department {
  id?: number;
  packageName: string;
  districtCode: string;
  districtName: string;
  state: string;
  status: boolean;
  createBy?: string;
  updatedBy?: string;
  createDate?: string;
  updateDate?: string;
}

export interface PaginatedResponse<T> {
  count: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const fetchDepartment = createAsyncThunk(
  "department/fetchDepartment",
  async ({
    page,
    pageSize,
    search,
    sort,
    order,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: ColumnFiltersState;
  }) => {
    try {
      const params: Record<string, any> = {
        page,
        page_size: pageSize,
      };

      if (search) params.search = search?.trim() || "";
      // Sort key map for frontend-to-backend translation
      const sortKeyMap: Record<string, string> = {
        organization_name: "organization.organization_name",
      };

      if (sort) {
        const backendSortKey = sortKeyMap[sort] || sort;
        params.ordering =
          order === "desc" ? `-${backendSortKey}` : backendSortKey;
      }

      if (filters && Array.isArray(filters)) {
        filters.forEach((filter) => {
          const { id, value } = filter;
          if (!id || value === undefined || value === "") return;
          if (
            (id === "updated_at" || id === "created_at") &&
            typeof value === "object"
          ) {
            const { from_date, to_date } = value as {
              from_date?: string;
              to_date?: string;
            };
            if (id === "updated_at") {
              if (from_date) params["updated_at_from"] = from_date;
              if (to_date) params["updated_at_to"] = to_date;
            }
            if (id === "created_at") {
              if (from_date) params["created_at_from"] = from_date;
              if (to_date) params["created_at_to"] = to_date;
            }
          } else {
            params[id] = value;
          }
        });
      }

      // const response: any = await Axios.get(`${API_ENDPOINT?.USER_MANAGEMENT?.DEPARTMENT?.LIST}?`, { params });
      const response: any = await Axios.get(
        `${API_ENDPOINT?.USER_MANAGEMENT?.DEPARTMENT?.LIST}`,
        { params }
      );

      const responseData = response.data;

      return {
        data: responseData?.data || responseData?.results || [],
        rowCount: responseData?.total_records || 0,
      };
    } catch (error: any) {
      console.error(
        "fetchDepartment error:",
        error.response?.data || error.message
      );
      throw new Error("Failed to fetch departments");
    }
  }
);

export const fetchDeptById = createAsyncThunk(
  "department/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.USER_MANAGEMENT?.DEPARTMENT?.DETAIL(id)}`
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch department by ID"
      );
    }
  }
);

export const updateDepartment = createAsyncThunk(
  "department/updateDepartment",
  async (
    {
      id,
      depData,
    }: {
      id: string;
      depData: any;
    },
    { rejectWithValue }
  ) => {
    try {
       const response: any = await Axios.put(
      `${API_ENDPOINT?.USER_MANAGEMENT?.DEPARTMENT?.UPDATE(id)}`,
      depData
    );
    return response.data;
    } catch (error:any) {
      return rejectWithValue(
        error.response?.data || "Failed to Update department"
      );
    }
  }
);

export const updateStatusInDepartment = createAsyncThunk(
  "department/updateStatusInDepartment",
  async (data: any,{rejectWithValue}) => {


    const departmentId = data.department_id || data.departmentId;

    if (!departmentId) {
      throw new Error(" department_id is missing");
    }
    try {
      
      const response = await Axios.put(
        `${API_ENDPOINT?.USER_MANAGEMENT?.DEPARTMENT?.STATUS_UPDATE(
          departmentId
        )}`,
        data
      );
      return response.data;
    } catch (error:any) {
       return rejectWithValue(
        error.response?.data || "Failed to Update department"
      );
    }
  }
);

export const addDepartment = createAsyncThunk(
  "department/addDepartment",
  async (packageData: any, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        API_ENDPOINT.USER_MANAGEMENT.DEPARTMENT.CREATE,
        packageData
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to add department"
      );
    }
  }
);

export const createDepartment = createAsyncThunk(
  "department/createDepartment",
  async (packageData: any, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        API_ENDPOINT.USER_MANAGEMENT.DEPARTMENT.CREATE,
        packageData
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to add department"
      );
    }
  }
);

export const deleteDepartment = createAsyncThunk(
  "department/delete",
  async (id: string, { rejectWithValue }) => {
    try {
      await axios.delete(
        `${API_ENDPOINT?.USER_MANAGEMENT?.DEPARTMENT?.DELETE(id)}`,
        { headers }
      );
      return id;
    } catch (err: any) {
      console.error("Delete error:", err);
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const fetchDeptDropDown = createAsyncThunk(
  "department/fetchDeptDropDown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.USER_MANAGEMENT?.DEPARTMENT?.DROPDOWN}`
      );
      return response?.data?.data;
    } catch (err: any) {
      console.error("fetchOrgDropDown error:", err);
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const fetchDepartmentsByOrganisationDropDown = createAsyncThunk(
  "department/fetchDeptDropDown",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.USER_MANAGEMENT?.DEPARTMENT?.BY_ORG(id)}`
      );
      return response?.data;
    } catch (err: any) {
      console.error("fetchOrgDropDown error:", err);
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const fetchAllDepartmentsDropdowns = createAsyncThunk(
  "organization/fetchAllDepartmentsDropdowns",
  async () => {
    try {
      const [departmentRes, orgRes] = await Promise.all([
        Axios.get(`/department/get-all/`, { headers }),
        Axios.get(`/organization/all-org/`, { headers }),
      ]);

      return {
        orgDropDown: Array.isArray(orgRes.data.data)
          ? orgRes.data.data.map((item: any) => item.organization_name)
          : [],
        departDropdown: Array.isArray(departmentRes.data.data)
          ? Array.from(
              new Set(
                departmentRes.data.data.map((d: any) => d.department_name)
              )
            )
          : [],
      };
    } catch (err) {
      console.error("fetchAllDepartmentsDropdowns error:", err);
      throw err;
    }
  }
);

export const exportDepartmentsCSV = createAsyncThunk(
  "department/exportCSV",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("authToken");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "*/*",
      };
      // Use responseType: 'blob' to get the file as a blob
      const response = await Axios.get(
        `${API_ENDPOINT?.USER_MANAGEMENT?.DEPARTMENT?.EXPORT}`
      );
      // Return the blob and filename (if any)
      return {
        blob: response.data,
        filename:
          response.headers["content-disposition"]
            ?.split("filename=")[1]
            ?.replace(/"/g, "") || "departments.csv",
      };
    } catch (err: any) {
      console.error("Export CSV error:", err);
      return rejectWithValue(err.message || "Failed to export CSV");
    }
  }
);

export const DepartmentExportsCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: any;
}) => {
  const params = new URLSearchParams();
  params.append("export", "excel");

  if (search) params.append("search", search);

  if (filters && filters.length > 0) {
    filters.forEach(({ id, value }: { id: string; value: any }) => {
      if (id && value !== undefined && value !== null) {
        if (typeof value === "object" && value !== null) {
          const maybeDateFilter = value as {
            from_date?: string;
            to_date?: string;
          };

          if (
            (maybeDateFilter.from_date || maybeDateFilter.to_date) &&
            (id === "created_at" || id === "updated_at")
          ) {
            if (maybeDateFilter.from_date) {
              params.append(`${id}_from`, maybeDateFilter.from_date);
            }
            if (maybeDateFilter.to_date) {
              params.append(`${id}_to`, maybeDateFilter.to_date);
            }
            return;
          }

          Object.entries(value).forEach(([key, val]) => {
            if (val !== undefined && val !== null && val !== "") {
              params.append(key, String(val));
            }
          });
        } else {
          params.append(id, String(value));
        }
      }
    });
  }

  const response = await Axios.get(
    `${API_ENDPOINT?.USER_MANAGEMENT?.DEPARTMENT?.EXPORT}?${params.toString()}`,
    {
      responseType: "blob",
    }
  );

  return {
    blob: response.data,
    filename: `${
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "Department"
    }_${new Date().toISOString().split("T")[0]}.csv`,
  };
};
